import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductCard } from '../../components/ProductCard/ProductCard';
import { NavigationTabs } from '../../components/NavigationTabs/NavigationTabs';
import { fetchProducts, toggleFavorite } from '../../api/productApi';
import { Product } from '../../types/product';
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator';
import styles from './Favorites.module.scss';

export const Favorites: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'products' | 'favorites' | 'account'>('favorites');
    const navigate = useNavigate();

    useEffect(() => {
        const loadProducts = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const data = await fetchProducts();
                setProducts(data);
            } catch (err) {
                setError('Не удалось загрузить товары');
                console.error('Error fetching products:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadProducts();
    }, []);

    const handleToggleFavorite = useCallback(async (productId: string) => {
        try {
            await toggleFavorite(productId);
            setProducts(prevProducts =>
                prevProducts.map(product =>
                    product.id === productId
                        ? { ...product, isFavorite: !product.isFavorite }
                        : product
                )
            );
        } catch (err) {
            console.error('Error toggling favorite:', err);
        }
    }, []);

    const filteredProducts = useMemo(() => {
        return products.filter(p => p.isFavorite);
    }, [products]);

    const handleTabChange = (tab: 'products' | 'favorites' | 'account') => {
        setActiveTab(tab);
        if (tab === 'products') {
            navigate('/');
        } else if (tab === 'account') {
            navigate('/account');
        }
    };

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <LoadingIndicator message="Загрузка избранных товаров..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <div className={styles.error}>
                    {error}
                    <button
                        onClick={() => window.location.reload()}
                        className={styles.retryButton}
                    >
                        Попробовать снова
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.favoritesPage}>
            <div className={styles.content}>
                <div className={styles.productsGrid}>
                    {filteredProducts.length > 0 ? (
                        filteredProducts.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                className={styles.productCard}
                                onToggleFavorite={handleToggleFavorite}
                            />
                        ))
                    ) : (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyStateIcon}>♥</div>
                            <h3>В избранном пока нет товаров</h3>
                            <p>Добавляйте товары в избранное, чтобы они появились здесь</p>
                            <button
                                onClick={() => navigate('/')}
                                className={styles.browseButton}
                            >
                                Перейти к товарам
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.footer}>
                <NavigationTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </div>
        </div>
    );
};